"use client";

import { ReactNode } from "react";
import {
  Dialog as HeadlessDialog, // MUST alias — the exported component is also named Dialog
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { cn } from "../lib/utils";

const dialogIconColors = {
  red: { bg: "bg-red-100", text: "text-red-600" },
  blue: { bg: "bg-blue-100", text: "text-blue-600" },
  green: { bg: "bg-green-100", text: "text-green-600" },
  yellow: { bg: "bg-yellow-100", text: "text-yellow-600" },
  gray: { bg: "bg-gray-100", text: "text-gray-600" },
} as const;

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Standardized header title. Rendered as a DialogTitle above the children. */
  title?: ReactNode;
  /** iconoir class (e.g. "iconoir-warning-triangle") shown in a colored badge beside the title. */
  icon?: string;
  /** Badge color; defaults to blue. */
  iconColor?: keyof typeof dialogIconColors;
  /**
   * "fullPage" fills (almost) the whole viewport with a fixed header and a
   * scrolling body — for multi-step flows the user must finish in one sitting.
   */
  variant?: "default" | "fullPage";
  /** Removes the X so the dialog can only close programmatically. */
  hideCloseButton?: boolean;
  children: ReactNode;
}

const Dialog = ({
  isOpen,
  onClose,
  title,
  icon,
  iconColor = "blue",
  variant = "default",
  hideCloseButton = false,
  children,
}: DialogProps) => {
  const colors = dialogIconColors[iconColor];

  if (variant === "fullPage") {
    return (
      // Backdrop click / Esc never dismiss (user decision) — the X button is
      // the only shell-level close. onClose is wired to the X button, NOT here.
      <HeadlessDialog
        open={isOpen}
        as="div"
        className="relative z-50"
        onClose={() => {}}
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
        <div className="fixed inset-0 flex items-center justify-center p-1">
          <DialogPanel className="m-2 w-full md:w-2/3 h-full md:h-4/5 bg-white rounded-md shadow-xs flex flex-col overflow-hidden">
            <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-200">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900"
              >
                {title}
              </DialogTitle>
              {!hideCloseButton && (
                <button
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-600"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <span className="iconoir-xmark text-2xl" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">{children}</div>
          </DialogPanel>
        </div>
      </HeadlessDialog>
    );
  }

  return (
    // Backdrop click / Esc never dismiss (user decision) — the X button is the
    // only shell-level close. onClose is wired to the X button below, NOT here.
    <HeadlessDialog
      open={isOpen}
      as="div"
      className="relative z-50"
      onClose={() => {}}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-left sm:p-0">
          <DialogPanel className="relative transform rounded-md bg-white px-4 pb-4 pt-5 text-left shadow-xs transition-all sm:my-8 w-full sm:max-w-2xl sm:p-6">
            {!hideCloseButton && (
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-600"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <span className="iconoir-xmark text-2xl" aria-hidden="true" />
                </button>
              </div>
            )}
            {title ? (
              <div className="sm:flex sm:items-start">
                {icon && (
                  <div
                    className={cn(
                      "mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10",
                      colors.bg,
                    )}
                  >
                    <span
                      className={cn(icon, "text-xl", colors.text)}
                      aria-hidden="true"
                    />
                  </div>
                )}
                <div
                  className={cn(
                    "w-full mt-3 text-center sm:mt-0 sm:text-left",
                    icon && "sm:ml-4",
                  )}
                >
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    {title}
                  </DialogTitle>
                  {children}
                </div>
              </div>
            ) : (
              children
            )}
          </DialogPanel>
        </div>
      </div>
    </HeadlessDialog>
  );
};

export { Dialog, type DialogProps };
